import {Card, Col, ConfigProvider, Row, Table, Tag, theme, Typography} from 'antd';
import {UserOutlined} from "@ant-design/icons";
import React from 'react';
import './Match.css'
import './Team/Team.css';
import {DomExt, ITeam} from "./Team/ITeam";
import Team from "./Team/Team";
import Scores from "./Scores/Scores";
import {HasMatchResults, HasMatchStarted, IMatch, IsMatchInProgress} from "../Interfaces/IMatch";
import {useGlobal} from "../config/GlobalContext";
import {useAuth} from "../config/AuthContext";
import {IUserBets} from "../Interfaces/IUserBets";
import {displayMatchDate, displayMatchTime} from "../Services/datetime";
import {MdOutlineStadium} from "react-icons/md";
import {LiaMapSignsSolid} from "react-icons/lia";
import MarqueeText from "./MarqueeText";
import MatchRandomBetButton from "./MatchRandomBetButton";

const {Text} = Typography;
const noWrap = {whiteSpace: 'nowrap' as const, fontSize: 11};

export type MatchPointsRow = {
    cmpedt_idt: number;
    match_num: number;
    usr_idt: number;
    parieur: string;
    total: number;
};

export type MatchBetRow = {
    match_num: number;
    usr_idt: number;
    parieur: string;
    score_dom: number | null;
    score_ext: number | null;
};

export type LeaderboardRow = {
    cmpedt_idt: number;
    usr_idt: number;
    parieur: string;
    total: number | string;
};

type RankRowDisplay = {
    rang: number;
    usr_idt: number;
    parieur: string;
    matchTotal: number | null;
    overallTotal: number;
    bet: string | null;
    isMe: boolean;
};

const rankCol = {title: '#', dataIndex: 'rang', width: 26, align: 'center' as const, onHeaderCell: () => ({style: noWrap}), onCell: () => ({style: noWrap})};
const parieurCol = {
    title: 'Parieur',
    dataIndex: 'parieur',
    onCell: () => ({style: {maxWidth: 0, overflow: 'hidden', fontSize: 11}}),
    render: (v: string, row: RankRowDisplay) => (
        <div style={{display: 'flex', alignItems: 'center', gap: 4, minWidth: 0}}>
            <div style={{flex: 1, minWidth: 0}}>
                {row.isMe ? (
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#42a5f5',
                        whiteSpace: 'nowrap',
                    }}>
                        <UserOutlined/>Vous
                    </span>
                ) : (
                    <MarqueeText text={v ?? ''} fontSize={11}/>
                )}
            </div>
            {row.bet && (
                <span
                    title={`Pari : ${row.bet}`}
                    style={{
                        flexShrink: 0,
                        fontSize: 10,
                        padding: '0 4px',
                        borderRadius: 4,
                        background: 'rgba(255,255,255,0.08)',
                        color: 'rgba(255,255,255,0.9)',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {row.bet}
                </span>
            )}
        </div>
    ),
};
const matchCol = {
    title: 'M.',
    dataIndex: 'matchTotal',
    width: 38,
    align: 'right' as const,
    onHeaderCell: () => ({style: noWrap}),
    onCell: () => ({style: noWrap}),
    render: (v: number | null) => (v == null ? <Text style={{fontSize: 11, opacity: 0.45}}>—</Text> : <Text style={{fontSize: 11}}>{v}</Text>),
};
const totalCol = {
    title: 'Tot.',
    dataIndex: 'overallTotal',
    width: 44,
    align: 'right' as const,
    onHeaderCell: () => ({style: noWrap}),
    onCell: () => ({style: noWrap}),
    render: (v: number) => <Text strong style={{fontSize: 11}}>{Number(v)}</Text>,
};

const columnsWithMatch = [rankCol, parieurCol, matchCol, totalCol];
const columnsTotalOnly = [rankCol, parieurCol, totalCol];

interface ThisProps {
    match: IMatch,
    userBet?: IUserBets;
    pointsRows?: MatchPointsRow[];
    betsByUser?: Map<number, MatchBetRow>;
    leaderboard?: LeaderboardRow[];
    cumulativeByUser?: Map<number, number>;
    isNextUpcoming?: boolean;
}

const MatchComponent: React.FC<ThisProps> = ({match, userBet, pointsRows = [], betsByUser, leaderboard = [], cumulativeByUser, isNextUpcoming = false}) => {

    const teamDom: ITeam = {
        Idt: match.team_idt_Dom,
        Nom: match.Nom_Dom,
        DomExt: DomExt.DOM,
        Color: match.Team_Clr_Dom,
    }
    const teamExt: ITeam = {
        Idt: match.team_idt_Ext,
        Nom: match.Nom_Ext,
        DomExt: DomExt.EXT,
        Color: match.Team_Clr_Ext
    }

    const {showOld} = useGlobal();
    const {authUser} = useAuth();
    const group = ((match.Grp_Cod) ? `Groupe ${match.Grp_Cod}, ` : '');

    match.BetScore_Dom = userBet?.matchteam_idt === match.MatchTeam_Idt_Dom ? userBet.score : undefined;
    match.BetScore_Ext = userBet?.matchteam_idt === match.MatchTeam_Idt_Ext ? userBet.score : undefined;

    const matchStarted = HasMatchStarted(match);
    const matchHasResults = HasMatchResults(match);
    const matchKnown = matchStarted || matchHasResults;
    const showPointsCard = (matchKnown || isNextUpcoming) && leaderboard.length > 0;

    const orderedRows = React.useMemo<RankRowDisplay[]>(() => {
        if (!showPointsCard) return [];
        const me = authUser?.idt;
        const perMatchByUser = new Map<number, number>();
        for (const r of pointsRows) {
            perMatchByUser.set(Number(r.usr_idt), Number(r.total));
        }
        // The ranking column shows the cumulative total *up to this match*
        // (inclusive if the match has been played, exclusive otherwise), so
        // an old match never folds in points earned on later matches.
        const sorted = [...leaderboard]
            .map(r => {
                const key = Number(r.usr_idt);
                const total = cumulativeByUser ? (cumulativeByUser.get(key) ?? 0) : Number(r.total);
                return {...r, total};
            })
            .sort((a, b) => b.total - a.total || a.parieur.localeCompare(b.parieur));
        return sorted.map((r, i) => {
            const key = Number(r.usr_idt);
            const bet = matchKnown ? betsByUser?.get(key) : undefined;
            const betLabel = (bet && bet.score_dom != null && bet.score_ext != null)
                ? `${bet.score_dom}-${bet.score_ext}`
                : null;
            return {
                rang: i + 1,
                usr_idt: key,
                parieur: r.parieur,
                matchTotal: matchKnown ? (perMatchByUser.get(key) ?? 0) : null,
                overallTotal: r.total,
                bet: betLabel,
                isMe: !!me && key === Number(me),
            };
        });
    }, [showPointsCard, pointsRows, betsByUser, leaderboard, matchKnown, authUser?.idt, cumulativeByUser]);

    // A match counts as "old" (= hidden behind the Montrer les anciens
    // matchs toggle) only once it's fully done: started, scored, AND
    // past the H+120 in-progress cushion. Live-scoring a match no
    // longer evicts it from the list while it's still being played.
    if (!showOld && HasMatchStarted(match) && HasMatchResults(match) && !IsMatchInProgress(match)) {
        return <></>;
    }

    return (
        <>
            <div style={{position: 'relative', overflow: 'hidden'}}>
                {showPointsCard && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 8,
                            bottom: 8,
                            left: 0,
                            right: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            overflow: 'hidden',
                        }}
                    >
                        <div style={{width: '100%', maxWidth: 260, maxHeight: '100%', overflowY: 'auto'}}>
                            <ConfigProvider theme={{algorithm: theme.darkAlgorithm}}>
                                <div style={{display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4}}>
                                    <Tag style={{margin: 0, fontWeight: 600, minWidth: 22, textAlign: 'center'}}>
                                        {match.Num}
                                    </Tag>
                                    <Text strong style={{fontSize: 13, color: '#fff'}}>
                                        {matchKnown ? 'Points' : 'Classement (à venir)'}
                                    </Text>
                                </div>
                                <div style={{borderRadius: '0 0 8px 8px', overflow: 'hidden'}}>
                                    <Table
                                        size="small"
                                        pagination={false}
                                        showHeader
                                        dataSource={orderedRows}
                                        rowKey="usr_idt"
                                        columns={(matchKnown ? columnsWithMatch : columnsTotalOnly) as any}
                                    />
                                </div>
                            </ConfigProvider>
                        </div>
                    </div>
                )}
            </div>
            <div className="MatchCard">
                <Card className={"MatchCard"}
                      title={<span>Match n° {match.Num}, le {displayMatchDate(match.Dte, match.Hre)} à {displayMatchTime(match.Dte, match.Hre)}<br/>
                          {match.Journee_Lib}<br/>
                          <MdOutlineStadium /> {group}{match.Stade_Nom} | {match.Ville_Nom} <LiaMapSignsSolid /></span>}
                      extra={<MatchRandomBetButton match={match}/>}
                      bordered={false}>
                    <Row>
                        <Col span={8}>
                            <Team team={teamDom}/>
                        </Col>
                        <Col span={8}>
                            <Scores match={match}/>
                        </Col>
                        <Col span={8}>
                            <Team team={teamExt}/>
                        </Col>
                    </Row>
                </Card>
            </div>
        </>
    );
}
export default MatchComponent;
